<template>
  <v-form :lazy="true" @submit.prevent>
    <h2>Loan Details</h2>
    <p>Deal Size</p>
    <p class="help">Please select all that apply</p>

    <label>Minimum Deal Size</label>
    <v-text-field
      prefix="$"
      label="200,000"
      solo
      v-mask="dealMinMask"
      v-model="productData.dealSize.min"
      @input="updateField('min', $event)" />

    <label>Maximum Deal Size</label>
    <v-text-field
      prefix="$"
      label="1,000,000"
      solo
      v-mask="dealMaxMask"
      v-model="productData.dealSize.max"
      @input="updateField('max', $event)" />

    <div class="grid-wrapper">
      <div>
        <label>Primary Loan Type</label>
        <v-select
          :value="product.loanType"
          :items="loanTypes"
          item-text="title" item-value="name"
          solo
          @change="$emit('change', { field: 'loanType', value: $event })" />
      </div>

      <div class="switch-wrapper">
        <label>
          <span class="help">Custom text</span>
        </label>
        <v-switch
          v-model="showCustomLoanType"
          @change="updateField('showCustomLoanType', $event)" />
      </div>

      <div>
        <template v-if="showCustomLoanType">
          <label>Custom Text: Loan Type</label>
          <v-text-field
            label="(Optional) custom text to display on Landing Page"
            :value="product.loanTypeCustomDescription"
            solo
            @change="$emit('change', { field: 'loanTypeCustomDescription', value: $event })" />
        </template>
      </div>

      <div>
        <label>Loan Repayment Terms</label>
        <v-select
          :value="product.loanPaymentTerms"
          :items="loanRepaymentTypes"
          item-text="title" item-value="name"
          solo
          :rules="required"
          @change="$emit('change', { field: 'loanPaymentTerms', value: $event })" />
      </div>

      <div class="switch-wrapper">
        <label>
          <span class="help">Custom text</span>
        </label>
        <v-switch
          v-model="showCustomPaymentTerms"
          @change="updateField('showCustomPaymentTerms', $event)" />
      </div>

      <div>
        <template v-if="showCustomPaymentTerms">
          <label>Custom Text: Loan Repayment Terms</label>
          <v-text-field
            label="(Optional) custom text to display on Landing Page"
            :value="product.loanPaymentTermsCustomDescription"
            solo
            @change="$emit('change', { field: 'loanPaymentTermsCustomDescription', value: $event })" />
        </template>
      </div>

      <div>
        <label>Loan Interest Rate</label>
        <v-text-field
          label="Ex 5%"
          :value="product.loanInterestRate"
          :rules="[
            ...required,
            v => v >= 0 || 'Must be at least 0',
            v => v <= 100 || 'Must be less than or equal to 100',
          ]"
          solo
          @change="$emit('change', {
            field: 'loanInterestRate',
            value: parseInt($event.replace(/%/g, ''))
          })" />
      </div>

      <div class="switch-wrapper">
        <label>
          <span class="help">Custom text</span>
        </label>
        <v-switch
          v-model="showCustomInterestRate"
          @change="updateField('showCustomInterestRate', $event)" />
      </div>

      <div>
        <template v-if="showCustomInterestRate">
          <label>Custom Text: Loan Interest Rate</label>
          <v-text-field
            label="(Optional) custom text to display on Landing Page"
            :value="product.loanInterestRateCustomDescription"
            solo
            @change="$emit('change', { field: 'loanInterestRateCustomDescription', value: $event })" />
        </template>
      </div>

      <div>
        <label>Min Credit Score</label>
        <v-text-field
          label="Ex 650"
          type="number"
          :value="product.creditScore"
          :rules="[
            ...required,
            v => v > 0 || 'Must be greater than 0',
            v => v < 1000 || 'Must be less than 1000',
          ]"
          solo
          @change="$emit('change', { field: 'creditScore', value: $event })" />
      </div>

      <div /><div />

      <div>
        <label>Speed of Close <span class="help">(days)</span></label>
        <v-text-field
          label="Ex 30"
          type="number"
          :value="product.speedOfClosing"
          :rules="[
            ...required,
            v => v > 0 || 'Must be greater than 0',
          ]"
          solo
          @change="$emit('change', { field: 'speedOfClosing', value: $event })" />
      </div>

      <div /><div />
    </div>
  </v-form>
</template>

<script>
import { mapState } from 'vuex'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { formMixin } from '@/mixins'

const currencyMask = createNumberMask({
  prefix: '',
  includeThousandsSeparator: true,
  allowNegative: false,
  integerLimit: 18,
})

export default {
  name: 'SectionLoan',
  mixins: [formMixin],
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    dealMinMask: currencyMask,
    dealMaxMask: currencyMask,
    showCustomLoanType: false,
    showCustomPaymentTerms: false,
    showCustomInterestRate: false,
    productData: {
      dealSize: {},
    },
  }),

  computed: {
    ...mapState('questionnaireStore', {
      loanRepaymentTypes: ({ questions }) => questions ? questions.loanRepaymentTypes : [],
      loanTypes: ({ questions }) => questions ? questions.loanTypes : [],
    }),
  },

  watch: {
    product () {
      if (this.product.loanTypeCustomDescription) this.showCustomLoanType = true
      if (this.product.loanPaymentTermsCustomDescription) this.showCustomPaymentTerms = true
      if (this.product.loanInterestRateCustomDescription) this.showCustomInterestRate = true
    },
  },

  created () {
    this.productData = {
      dealSize: {
        min: this.product.dealSize.min,
        max: this.product.dealSize.max,
      },
    }

    if (this.product.loanTypeCustomDescription) this.showCustomLoanType = true
    if (this.product.loanPaymentTermsCustomDescription) this.showCustomPaymentTerms = true
    if (this.product.loanInterestRateCustomDescription) this.showCustomInterestRate = true
  },

  methods: {
    updateField (field, value) {
      if (field === 'min' || field === 'max') {
        this.$emit('change', {
          field: 'dealSize',
          value: {
            min: this.productData.dealSize.min,
            max: this.productData.dealSize.max,
          },
        })
      }

      if (field === 'showCustomLoanType' && !value) {
        this.$emit('change', { field: 'loanTypeCustomDescription', value: '' })
      }

      if (field === 'showCustomInterestRate' && !value) {
        this.$emit('change', { field: 'loanInterestRateCustomDescription', value: '' })
      }

      if (field === 'showCustomPaymentTerms' && !value) {
        this.$emit('change', { field: 'loanPaymentTermsCustomDescription', value: '' })
      }
    },

    fieldChanged (field, value) {
      this.$emit('change', { field, value })
    },
  },
}
</script>

<style lang="scss" scoped>
.grid-wrapper {
  grid-template-columns: 1fr auto 1fr;
  grid-gap: 20px 30px;
  align-items: center;
  justify-content: center;

  & > label:first-of-type {
    margin-bottom: -30px !important;
  }
}

.switch-wrapper {
  margin-top: -15px;
}

.v-input--switch {
  margin-top: -5px;
  margin-bottom: 10px;

  ::v-deep {
    .v-input__slot {
      justify-content: center;
    }
  }
}
</style>
